import { Component, OnInit } from '@angular/core';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { ResearchApiService } from '../../services/research-api.service';
import { PortalConfigService } from '../../services/portal-config.service';
import { ResearchPersonProfile, ResearchPortalConfig } from '../../models/research.models';
import { DescribeError } from '../shared/describe-error';
import { LoadFailures } from '../shared/load-failures';
import { PageErrorCopy, StatePanelCopy } from '../shared/state-copy';

/**
 * Preferences — Task 22. A read-only map of "what the portal knows about
 * you, and where each part is managed": three destination tiles (member
 * portal profile, AGU public profile, account settings) plus a mirror of the
 * member's own identity data (`researchMyProfile`) with no edit affordances,
 * since every field here is actually owned and changed on agu.org (spec D:
 * managed on agu.org).
 *
 * Root component of the `settings` module — rendered via `ngComponentOutlet`
 * with no inputs, same as every other RA module; every dependency comes
 * through constructor injection from the surface's providers, including
 * `PortalConfigService`, which is memoised per surface instance (shared with
 * `RaPersonProfileComponent`/`RaSurfaceComponent` — see that service's own
 * doc comment), so this page's `Load()` never pays for a second
 * `researchPortalConfig` round trip.
 *
 * Product states (PD §5): 3 skeleton cards while loading (P/proto-states.js:130) and the error panel when the load fails
 * (state-copy.ts `PageErrorCopy`). The prototype's save-failure banner (:123) has no counterpart: nothing on this page is saved.
 */
@Component({
    standalone: false,
    selector: 'ra-settings',
    templateUrl: './settings-page.component.html',
    styleUrl: './settings-page.component.scss',
    // The mockup's `.main` is a full-bleed track with 32px of gutter and its
    // `.grid` is a single `1fr` column (settings.html:313), so the cards are
    // the width of the page rather than a 720px column centred inside it.
    host: { class: 'ra-fullwidth' },
})
export class SettingsPageComponent implements OnInit {
    profile: ResearchPersonProfile | null = null;
    config: ResearchPortalConfig | null = null;

    loading = false;
    error: string | null = null;
    /** The error panel's foot, retry note and Contact support link (PD §5). */
    readonly failures = new LoadFailures();
    readonly ErrorCopy: StatePanelCopy = PageErrorCopy('your settings');

    constructor(
        private readonly api: ResearchApiService,
        private readonly portalConfig: PortalConfigService,
        private readonly host: ExplorerRaHostAdapter
    ) {}

    ngOnInit(): void {
        void this.failures.ReadSupport(this.portalConfig);
        void this.Load();
    }

    async Load(): Promise<void> {
        this.loading = true;
        this.error = null;
        try {
            const [profile, config] = await Promise.all([this.api.MyProfile(), this.portalConfig.Load()]);
            this.profile = profile;
            this.config = config;
            this.failures.Reset();
        } catch (e) {
            this.error = DescribeError(e);
            this.failures.Record(e);
        } finally {
            this.loading = false;
        }
    }

    Retry(): void {
        void this.Load();
    }

    /** "View my portal profile" tile — the one in-app destination among the three (the other two are external agu.org links), so it navigates rather than opening a new tab. */
    OnViewMyProfile(): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Profile, View: 'me' });
    }

    get PrimaryField(): string {
        return this.profile?.researchAreas[0] ?? '—';
    }

    get CareerStage(): string {
        return this.profile?.careerStage ?? '—';
    }

    get Institution(): string {
        return this.profile?.organization ?? '—';
    }

    get Email(): string {
        return this.profile?.email ?? '—';
    }

    /** "Member since {year}" once `membershipSince` is known, else the honest fallback — the portal has no membership status of its own to show (spec D: managed on agu.org). */
    get MembershipLine(): string {
        return this.profile?.membershipSince ? `Member since ${this.profile.membershipSince}` : 'Managed on agu.org';
    }
}
